import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { SecondaryMarketPresenter } from './secondary-market-presenter'
import {
  secondaryMarketServices,
  SecondaryMarketPortalData,
  SecondaryMarketOrder,
} from '../../services/secondary-market-service'
import { whatsAppService } from '../../services/whatsapp-support'
import { showBanner } from '../../global-state/banner-slice'

export const SecondaryMarketContainer = () => {
  const dispatch = useDispatch()
  const [portal, setPortal] = useState<SecondaryMarketPortalData | null>(null)
  const [activeTab, setActiveTab] = useState<'browse' | 'requests' | 'trades'>('browse')
  const [loading, setLoading] = useState(true)
  const [sellModalOpen, setSellModalOpen] = useState(false)
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<number | null>(null)
  const [sellQuantity, setSellQuantity] = useState('')
  const [priceBasis, setPriceBasis] = useState<'AT_REFERENCE' | 'LIMIT'>('AT_REFERENCE')
  const [limitPrice, setLimitPrice] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [buyModalOpen, setBuyModalOpen] = useState(false)
  const [buyListing, setBuyListing] = useState<SecondaryMarketOrder | null>(null)
  const [offerPrice, setOfferPrice] = useState('')

  const loadPortal = useCallback(async () => {
    setLoading(true)
    try {
      const data = await secondaryMarketServices.getPortal()
      setPortal(data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPortal()
  }, [loadPortal])

  const handleSellSubmit = async () => {
    if (!selectedOpportunityId || !sellQuantity) return
    setSubmitting(true)
    try {
      const res = await secondaryMarketServices.submitSell({
        opportunity_id: selectedOpportunityId,
        quantity: Number(sellQuantity),
        price_basis: priceBasis,
        ...(priceBasis === 'LIMIT' ? { limit_price: Number(limitPrice) } : {}),
      })
      if (res.status === 201) {
        dispatch(showBanner({ text: 'Sell request submitted', variant: 'success-banner' }))
        setSellModalOpen(false)
        setSellQuantity('')
        setLimitPrice('')
        loadPortal()
        setActiveTab('requests')
      } else {
        const message =
          (res as { data?: { message?: string } })?.data?.message ||
          'Could not submit sell request.'
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
      }
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not submit sell request.'
      dispatch(showBanner({ text: message, variant: 'error-banner' }))
    } finally {
      setSubmitting(false)
    }
  }

  const openBuyModal = (listing: SecondaryMarketOrder) => {
    setBuyListing(listing)
    const suggested =
      listing.price_basis === 'LIMIT' && listing.limit_price !== null
        ? String(listing.limit_price)
        : listing.reference_price !== null
        ? String(listing.reference_price)
        : ''
    setOfferPrice(suggested)
    setBuyModalOpen(true)
  }

  const closeBuyModal = () => {
    setBuyModalOpen(false)
    setBuyListing(null)
    setOfferPrice('')
  }

  const handleBuyInterest = async () => {
    if (!buyListing || !offerPrice) return
    setSubmitting(true)
    try {
      const res = await secondaryMarketServices.submitBuyInterest({
        sell_order_id: buyListing.id,
        quantity: buyListing.quantity,
        offer_price: Number(offerPrice),
      })
      if (res.status === 201) {
        dispatch(showBanner({ text: 'Buy interest submitted', variant: 'success-banner' }))
        closeBuyModal()
        loadPortal()
        setActiveTab('requests')
      } else {
        const message =
          (res as { data?: { message?: string } })?.data?.message ||
          'Could not submit buy interest.'
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
      }
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not submit buy interest.'
      dispatch(showBanner({ text: message, variant: 'error-banner' }))
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = async (order: SecondaryMarketOrder) => {
    const isBuyInterest = order.type === 'BUY_INTEREST'
    const confirmLabel = isBuyInterest
      ? 'Withdraw this buy interest?'
      : 'Withdraw this sell listing?'
    if (!window.confirm(confirmLabel)) return

    try {
      const res = await secondaryMarketServices.cancelOrder(order.id)
      if (res.status === 200) {
        dispatch(
          showBanner({
            text: isBuyInterest ? 'Buy interest withdrawn' : 'Sell listing withdrawn',
            variant: 'success-banner',
          }),
        )
        loadPortal()
      } else {
        const message =
          (res as { data?: { message?: string } })?.data?.message ||
          'Could not withdraw this request.'
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
      }
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not withdraw this request.'
      dispatch(showBanner({ text: message, variant: 'error-banner' }))
    }
  }

  const openSellModal = (opportunityId?: number) => {
    setSelectedOpportunityId(opportunityId ?? null)
    setSellModalOpen(true)
  }

  const handleContactRm = (trade?: SecondaryMarketOrder) => {
    if (trade) {
      whatsAppService.requestSecondaryMarketTradeSupport(trade.order_id, trade.opportunity_title)
      return
    }
    whatsAppService.requestSecondaryMarketSupport()
  }

  return (
    <SecondaryMarketPresenter
      portal={portal}
      loading={loading}
      activeTab={activeTab}
      sellModalOpen={sellModalOpen}
      selectedOpportunityId={selectedOpportunityId}
      sellQuantity={sellQuantity}
      priceBasis={priceBasis}
      limitPrice={limitPrice}
      submitting={submitting}
      buyModalOpen={buyModalOpen}
      buyListing={buyListing}
      offerPrice={offerPrice}
      onTabChange={setActiveTab}
      onOpenSellModal={openSellModal}
      onCloseSellModal={() => setSellModalOpen(false)}
      onSelectedOpportunityChange={setSelectedOpportunityId}
      onSellQuantityChange={setSellQuantity}
      onPriceBasisChange={setPriceBasis}
      onLimitPriceChange={setLimitPrice}
      onSellSubmit={handleSellSubmit}
      onOpenBuyModal={openBuyModal}
      onCloseBuyModal={closeBuyModal}
      onOfferPriceChange={setOfferPrice}
      onBuyInterestSubmit={handleBuyInterest}
      onCancelOrder={handleCancel}
      onContactRm={handleContactRm}
    />
  )
}
